import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { FaRegCopy, FaTrash, FaEdit, FaDownload } from "react-icons/fa";
import { fetchJsonFromBack, saveJsonToBack } from "../../../store/jsonSlice.js";
import Title from "../../../components/Title.js";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button.js";
import TextInput from "../../../components/TextInput.js";
import TextArea from "../../../components/TextArea.js";
import { deleteFile, uploadFile, copyFile } from "../../../services/api/jsonService.ts";
import ActionManage, { addTmpIdToAction, reindexActionTmpIds } from "../Action/actionManage.js";
import { convertToPng } from "../../../utils/imageUtils";
import axios from 'axios';

const StepsManage = (props) => {
  let routeParams = useParams();
  const rootFolder = props.rootFolder || routeParams.rootFolder;
  const filename = props.filename || routeParams.filename;
  const stepIndex = props.stepIndex || routeParams.stepIndex;

  const [showImgModal, setShowImgModal] = useState(false);
  const [modalImgUrl, setModalImgUrl] = useState(null);


  const dispatch = useDispatch();
  const detailsRef = useRef(null); // Référence pour le <details> dans ActionManage
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [Contents, setContents] = useState(getEmptyStep());
  const [editingStepIndex, setEditingStepIndex] = useState(null);

  // État pour les fichiers en attente
  const [pendingImg, setPendingImg] = useState(null);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [pendingHologram, setPendingHologram] = useState(null);
  const [previewImgUrl, setPreviewImgUrl] = useState(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);

  // fonction pour le défilement vers la zone de contenu
  const scrollToContentArea = (ElementId) => {
    setTimeout(() => {
      const anchor = document.getElementById(ElementId);
      if (anchor) anchor.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Récupérer l'état complet du store
  const {
    jsonData,
    loading: jsonLoading,
    error,
  } = useSelector((state) => ({
    jsonData: state.json?.jsonData || {
      process: {
        steps: [],
        stages: [],
        urlFolderName: "",
      },
    },
    loading: state.json?.loading || false,
    error: state.json?.error || null,
  }));

  // Initialiser les données avec des valeurs par défaut si nécessaire
  const process = jsonData?.process || {
    steps: [],
    stages: [],
    urlFolderName: "",
  };

  // S'assurer que steps est toujours un tableau
  const steps = Array.isArray(process?.steps) ? process.steps : [];

  // Sécuriser les steps pour éviter les undefined
  const validSteps = Array.isArray(steps) ? steps.filter((s) => s && typeof s.stepID !== "undefined") : [];

  // Compose le chemin dossier pour le backend
  // Dossier cible = dossier du json (comme pour hologramme)
  // Harmonisation stricte avec procedureEdit.js :
  let folderName = undefined;
  if (routeParams.folderName) {
    let base = routeParams.folderName.replace(/\.json$/, ''); // ex: cc_MP
    let chapter = routeParams.chapterName || base.split('_')[0]; // ex: cc
    folderName = `${chapter}/${base}`; // ex: cc/cc_MP
  } else if (rootFolder && filename) {
    folderName = `${rootFolder}/${filename}`;
  } else if (rootFolder) {
    folderName = rootFolder;
  }

  // Calculer dynamiquement le prochain stepID pour chaque ajout
  const getNextStepID = () => {
    if (!validSteps || validSteps.length === 0) return 1;
    const existingIds = validSteps.map((s) => parseInt(s.stepID) || 0);
    let nextId = 1;
    while (existingIds.includes(nextId)) {
      nextId++;
    }
    return nextId;
  };

  const parentFolder = window.location.pathname.split("/")[2];
  const jsonFilename = `${folderName}.json`;
  const isEditing = stepIndex && !isNaN(Number(stepIndex));

  const handleDeleteFile = async (fieldName) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer ce fichier ?\n\n" + "👉 " + fieldName)) return;

    setContents(prev => ({
      ...prev,
      [fieldName]: ""
    }));
    setMessage({ type: "success", text: ` supprimé avec succès !` });
  };

  const handleDownloadHologram = async (fileName) => {
    try {
      const response = await axios.get(`/api/upload/download-hologram/${folderName}`, {
        params: { 
          fileName,
          t: new Date().getTime() }, // ✅ "fileName" correspond à req.query.fileName
        responseType: 'blob',
        withCredentials: true
      });

      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'hologram.glb');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors du téléchargement du fichier.');
      console.error(err);
    }
  };

  const handleEditStep = (step) => {
    const idx = steps.findIndex((s) => s.stepID === step.stepID);
    setEditingStepIndex(idx);
    const actionsWithTmpId = (step.stepActions || []).map(addTmpIdToAction);
    setContents({ ...step, stepActions: actionsWithTmpId });
  };

  const handleUploadFile = async ({
    file,
    params,
    fieldName,
    suffix,
    defaultExt,
  }) => {
    if (!file || !params) {
      setMessage && setMessage({ type: "error", text: "Paramètres manquants pour l'upload." });
      return null;
    }
    try {
      const data = {
        file,
        folder: folderName,
        suffix,
        defaultExt,
        params
      };
      const res = await uploadFile(data);
      console.log('[UPLOAD] Succès', res);

      let fileName = null;
      if (params.length > 1) {
        fileName = `step-${params[0]}_action-${params[1]}_${suffix}${defaultExt}`;
      }
      else {
        fileName = `step-${params[0]}_${suffix}${defaultExt}`;
      }


      // ✅ Ne mettre à jour Contents que pour les champs du step
      if (!fieldName.startsWith("action")) {
        setContents(prev => ({
          ...prev,
          [fieldName]: fileName
        }));
      }

      setFile && setFile(null);
      setMessage && setMessage({ type: "success", text: "Fichier enregistré avec succès !" });

      // Retourner le nom du fichier pour ActionManager
      return fileName;

    } catch (err) {
      console.error('[UPLOAD] Erreur', err);
      setMessage && setMessage({ type: "error", text: "Erreur lors de l'upload du fichier." });
      return null;
    }
  };



  /********************Steps ********** */
  function getEmptyStep() {
    return {
      stepID: 0,
      stepTitle: "",
      stepDesc: "",
      stepImg: "",
      stepHologram: "",
      stepHologramOffsetPos: { x: 0, y: 0, z: 0 },
      stepHologramOffsetRot: { x: 0, y: 0, z: 0 },
      stepHologramScale: "1",
      stepHologramInteractable: false,
      stepHologramCalibrate: false,
      stepAudioText: "",
      stepAudio: "",
      stepNote: "",
      stepActions: [],
    };
  }

  function resetFormStates() {
    setContents(getEmptyStep());
    setEditingStepIndex(null);
    setPendingImg(null);
    setPendingAudio(null);
    setPendingHologram(null);
  }

  // Sauvegarde automatique dans le backend à chaque changement de steps
  useEffect(() => {
    if (steps && steps.length > 0) {
      dispatch(saveJsonToBack(jsonData));
    }
  }, [steps]);

  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (isEditing && steps.length === 0 && !hasFetched) {
      setLoading(true);
      dispatch(fetchJsonFromBack({ parentFolder, jsonFilename })).finally(
        () => {
          setLoading(false);
          setHasFetched(true);
        }
      );
    }
  }, [
    isEditing,
    steps.length,
    dispatch,
    parentFolder,
    jsonFilename,
    hasFetched,
  ]);

  // Live preview for step image when a new file is selected
  useEffect(() => {
    if (!pendingImg) {
      setPreviewImgUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingImg);
    setPreviewImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImg]);

  // Live preview for step audio when a new file is selected
  useEffect(() => {
    if (!pendingAudio) {
      setPreviewAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingAudio);
    setPreviewAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingAudio]);

  //UseEffect StepEdit
  useEffect(() => {
    if (isEditing && steps.length === 0) {
      setLoading(true);
      dispatch(fetchJsonFromBack({ folderName, filename })).finally(() =>
        setLoading(false)
      );
    }
  }, [isEditing, steps.length, dispatch, folderName, filename]);

  //use effect pour initialiser le formulaire d'édition si nécessaire
  useEffect(() => {
    if (isEditing && steps.length > 0) {
      const existing = steps.find((s) => s.stepID === parseInt(stepIndex, 10));
      if (existing) {
        const actionsWithTmpId = (existing.stepActions || []).map(addTmpIdToAction);// Ajoute un _tmpId à chaque action si absent
        setContents({ ...existing, stepActions: actionsWithTmpId });
      }
    }
  }, [isEditing, stepIndex, steps]);

  // Sécurise l'affichage du formulaire :
  const isReady = !isEditing || (steps.length > 0 && steps.some((s) => s.stepID === parseInt(stepIndex, 10)));
  if (!isReady) {
    return <div>Chargement des données…</div>;
  }

  const handleChange = (field, value) => {
    setContents((prev) => ({ ...prev, [field]: value }));
  };

  // Affichage d'un loader si édition et steps non prêts
  if (isEditing && (loading || steps.length === 0)) {
    return <div>Chargement des données…</div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const currentStepID = Contents.stepID || getNextStepID();

    // Créer une copie de Contents pour les modifications
    let updatedContents = { ...Contents };

    // Upload des fichiers et mise à jour des noms
    if (pendingImg) {
      const imgFileName = await handleUploadFile({
        file: pendingImg,
        params: [currentStepID],
        fieldName: "stepImg",
        suffix: "img",
        defaultExt: ".png"
      });
      if (imgFileName) {
        updatedContents.stepImg = imgFileName;
      }
    }

    if (pendingAudio) {
      const audioFileName = await handleUploadFile({
        file: pendingAudio,
        params: [currentStepID],
        fieldName: "stepAudio",
        suffix: "audio",
        defaultExt: ".ogg"
      });
      if (audioFileName) {
        updatedContents.stepAudio = audioFileName;
      }
    }

    if (pendingHologram) {
      const hologramFileName = await handleUploadFile({
        file: pendingHologram,
        params: [currentStepID],
        fieldName: "stepHologram",
        suffix: "hologram",
        defaultExt: ".glb"
      });
      if (hologramFileName) {
        updatedContents.stepHologram = hologramFileName;
      }
    }

    let copyErrors = 0;
    if (updatedContents.stepActions) {
      updatedContents.stepActions = await Promise.all(updatedContents.stepActions.map(async (action, index) => {
        const { _copyFrom, ...actionWithoutCopy } = action;
        let updatedAction = { ...actionWithoutCopy };

        // Action collée/dupliquée : on duplique ses fichiers sous le nom de sa nouvelle position
        // (sauf si le fichier a été remplacé ou supprimé entre-temps)
        if (_copyFrom) {
          const suffixes = { actionImg: "img", actionAudio: "audio", actionHologram: "hologram" };
          for (const [field, sourceName] of Object.entries(_copyFrom)) {
            if (action[field] !== sourceName) continue;
            const ext = sourceName.match(/\.[^.]+$/)?.[0] || "";
            const targetName = `step-${currentStepID}_action-${index + 1}_${suffixes[field]}${ext}`;
            try {
              await copyFile({ folder: folderName, from: sourceName, to: targetName });
              updatedAction[field] = targetName;
            } catch (err) {
              console.error(`[COPY] ${field} de l'action "${action.actionTitle}"`, err);
              updatedAction[field] = "";
              copyErrors++;
            }
          }
        }

        if (action.actionImg instanceof File) {
          const imgFileName = await handleUploadFile({
            file: action.actionImg,
            params: [currentStepID, index + 1],
            fieldName: "actionImg",
            suffix: "img",
            defaultExt: ".png"
          });
          if (imgFileName) updatedAction.actionImg = imgFileName;
        }

        if (action.actionAudio instanceof File) {
          const audioFileName = await handleUploadFile({
            file: action.actionAudio,
            params: [currentStepID, index + 1],
            fieldName: "actionAudio",
            suffix: "audio",
            defaultExt: ".ogg"
          });
          if (audioFileName) updatedAction.actionAudio = audioFileName;
        }

        if (action.actionHologram instanceof File) {
          const holoFileName = await handleUploadFile({
            file: action.actionHologram,
            params: [currentStepID, index + 1],
            fieldName: "actionHologram",
            suffix: "hologram",
            defaultExt: ".glb"
          });
          if (holoFileName) updatedAction.actionHologram = holoFileName;
        }

        return updatedAction;
      })
      );
    }

    // Validation des valeurs numériques (comme avant)
    const parseOrError = (val) => {
      if (typeof val !== 'string') return 0;
      const num = parseFloat(val.replace(',', '.'));
      return isNaN(num) ? null : num;
    };

    const posX = parseOrError(updatedContents.stepHologramOffsetPos.x);
    const posY = parseOrError(updatedContents.stepHologramOffsetPos.y);
    const posZ = parseOrError(updatedContents.stepHologramOffsetPos.z);
    const rotX = parseOrError(updatedContents.stepHologramOffsetRot.x);
    const rotY = parseOrError(updatedContents.stepHologramOffsetRot.y);
    const rotZ = parseOrError(updatedContents.stepHologramOffsetRot.z);

    if ([posX, posY, posZ, rotX, rotY, rotZ].includes(null)) {
      setMessage({ type: "error", text: "Veuillez saisir des valeurs numériques valides pour la position et la rotation de l'hologramme." });
      setLoading(false);
      return;
    }

    try {
      // Vérifier que jsonData est défini
      if (!jsonData) {
        console.error("Erreur: jsonData n'est pas défini");
        return;
      }

      // Créer une copie profonde des données existantes
      const existingData = JSON.parse(JSON.stringify(jsonData));

      // S'assurer que la structure de base existe
      if (!existingData.process) {
        existingData.process = {};
      }

      // Créer une copie profonde du process existant
      const updatedProcess = {
        ...existingData.process,
        steps: Array.isArray(existingData.process.steps)
          ? [...existingData.process.steps]
          : [],
        stages: Array.isArray(existingData.process.stages)
          ? [...existingData.process.stages]
          : [],
      };

      // Préparer la nouvelle étape avec TOUTES les données mises à jour
      const buildStepWithOrderedKeys = (stepObj) => {
        const {
          stepID,
          stepTitle,
          ...rest
        } = stepObj;
        return {
          stepID,
          stepTitle,
          ...rest
        };
      };

      // Utiliser updatedContents au lieu de Contents
      const newStep = buildStepWithOrderedKeys({
        ...updatedContents,
        stepHologramOffsetPos: { x: posX, y: posY, z: posZ },
        stepHologramOffsetRot: { x: rotX, y: rotY, z: rotZ },
        stepActions: reindexActionTmpIds(updatedContents.stepActions || [])
      });

      // Mettre à jour ou ajouter l'étape
      if (editingStepIndex !== null && updatedProcess.steps[editingStepIndex]) {
        // Mise à jour d'une étape existante
        updatedProcess.steps[editingStepIndex] = buildStepWithOrderedKeys({
          ...updatedProcess.steps[editingStepIndex],
          ...newStep,
          stepID: updatedProcess.steps[editingStepIndex].stepID, // Conserver l'ID existant
        });
      } else {
        // Ajout d'une nouvelle étape
        updatedProcess.steps.push(
          buildStepWithOrderedKeys({
            ...newStep,
            stepID: getNextStepID(),
          })
        );
      }

      // Mettre à jour les données avec la structure complète
      const updatedData = {
        ...existingData,
        process: updatedProcess,
        settings: existingData.settings || {},
      };

      // Mettre à jour le store Redux et sauvegarder
      dispatch({ type: "json/updateJsonData", payload: updatedData });
      await dispatch(saveJsonToBack(updatedData)); // ⚠️ Ajouter await ici aussi

      resetFormStates();
      setEditingStepIndex(null);

      setMessage(copyErrors > 0
        ? { type: "error", text: `Étape enregistrée, mais ${copyErrors} fichier(s) d'action copiée(s) introuvable(s) : à ré-importer.` }
        : { type: "success", text: "Étape enregistrée avec succès" });
      scrollToContentArea("stepsList");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'étape:", error);
      setMessage({
        type: "error",
        text: "Erreur lors de la sauvegarde de l'étape",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetFormStates();
    scrollToContentArea("stepsList");
    detailsRef.current.removeAttribute("open");
  };

  // Fonction pour dupliquer un step
  const handleDuplicateStep = (stepToDuplicate) => {
    const nextStepID = getNextStepID();
    const duplicatedStep = {
      ...stepToDuplicate,
      stepID: nextStepID,
      stepTitle: `${stepToDuplicate.stepTitle} (Copie)`,
    };
    const newSteps = [...steps, duplicatedStep];
    const updatedJson = {
      ...jsonData,
      process: { ...jsonData.process, steps: newSteps },
    };
    dispatch({ type: "json/updateJsonData", payload: updatedJson });
    dispatch(saveJsonToBack(updatedJson));
    resetFormStates();
  };

  // fonction pour supprimer un step
  const handleDeleteStep = (stepID) => {

    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer l'étape " + stepID + " ?\n\n❌ [" + (steps.find(s => s.stepID === stepID)?.stepTitle || "") + "]")) {
      return;
    }
    const stepToDelete = steps.find((step) => step.stepID === stepID);
    if (stepToDelete) {
      // Supprimer les fichiers du step lui-même
      if (stepToDelete.stepImg) {
        handleDeleteFile("stepImg");
      }

      if (stepToDelete.stepAudio) {
        handleDeleteFile("stepAudio");
      }

      if (stepToDelete.stepHologram) {
        handleDeleteFile("stepHologram");
      }

      // Supprimer les fichiers associés aux actions de ce step
      if (stepToDelete.stepActions) {
        stepToDelete.stepActions.forEach(action => {
          if (action.actionImg) {
            handleDeleteFile("actionImg");
          }

          if (action.actionAudio) {
            handleDeleteFile("actionAudio");
          }

          if (action.actionHologram) {
            handleDeleteFile("actionHologram");
          }
        });
      }
    }

    // Ensuite, supprimer le step du JSON et ré-indexer les IDs
    const filteredSteps = steps.filter((step) => step.stepID !== stepID);

    // Ré-indexer les stepID de 1 à N pour éviter les trous
    const newSteps = filteredSteps.map((step, index) => ({
      ...step,
      stepID: index + 1,
    }));

    // Mettre à jour les stagePath des stages (procédures)
    const updatedStages = (jsonData.process?.stages || []).map((stage) => {
      if (!stage.stagePath) return stage;
      
      const newPath = stage.stagePath
        .split('.')
        .map(Number)
        .filter((id) => id !== stepID)
        .map((id) => {
          if (id > stepID) {
            return id - 1;
          }
          return id;
        })
        .join('.');

      return {
        ...stage,
        stagePath: newPath,
      };
    });

    const updatedJson = {
      ...jsonData,
      process: { 
        ...jsonData.process, 
        steps: newSteps,
        stages: updatedStages
      },
    };

    dispatch({ type: "json/updateJsonData", payload: updatedJson });
    dispatch(saveJsonToBack(updatedJson));
    resetFormStates();
  };



  // ========================================
  // RENDER
  // ========================================

  return (

    <div className="form-container p-4">
      {/************************** ETAPES******************* */}
      {/*------------ LISTE DES ETAPES--------------*/}
      <a id="stepsList" style={{ marginTop: "-80px", paddingTop: "80px", display: "block" }}></a>
      <h3 className="text-primary text-center">Liste des étapes</h3>
      <div alt="Liste des étapes" className="card shadow-sm mb-4 border border-primary" style={{ maxHeight: "320px", overflowY: "scroll" }}>
        <table alt="stepsList" className="table">
          <thead className="table-light">
            <tr>
              <th className="text-tertiary">ID</th>
              <th className="text-tertiary">Titre</th>
              <th className="text-tertiary">Description</th>
              <th className="text-tertiary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {steps
              .filter(
                (step) => step && typeof step.stepID !== "undefined"
              )
              .map((step) => (
                <tr key={step.stepID}>
                  <td>{step.stepID}</td>
                  <td
                    style={{
                      width: "200px",
                      cursor: editingStepIndex !== null ? "not-allowed" : "pointer"
                    }}
                    className="fw-bold text-primary"
                    //onClick : si editingStepIndex est null, alors on peut éditer
                    onClick={() => {
                      if (editingStepIndex === null) {
                        handleEditStep(step);
                        scrollToContentArea("stepEdit");
                      }
                    }}
                    title={editingStepIndex !== null
                      ? "Déjà en édition\nVeuillez annuler ou de sauvegarder"
                      : "Modifier cette étape"}
                  >
                    {step.stepTitle}
                  </td>
                  <td>{step.stepDesc}</td>
                  {editingStepIndex == null ? (
                    <td style={{ width: "140px" }}>
                      <button
                        type="button"
                        title="Modifier l'étape"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          const idx = steps.findIndex(
                            (s) => s.stepID === step.stepID
                          );
                          setEditingStepIndex(idx);
                          /*setContents({ ...step });*/
                          const actionsWithTmpId = (step.stepActions || []).map(addTmpIdToAction);
                          setContents({ ...step, stepActions: actionsWithTmpId });
                          scrollToContentArea("stepEdit");
                          /*setTimeout(() => {
                            const anchor = document.getElementById("stepEdit");
                            if (anchor) {
                              const yOffset = 15; // Cette ligne permet de régler le décalage vertical
                              const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset; // Cette ligne permet de régler le décalage vertical
                              window.scrollTo({ top: y, behavior: "smooth" });
                            }
                          }, 100);*/
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        title="Dupliquer l'étape"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => handleDuplicateStep(step)}
                      >
                        <FaRegCopy />
                      </button>
                      <button
                        type="button"
                        title="Supprimer l'étape"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteStep(step.stepID)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  ) : (
                    <td style={{ width: "140px" }}>
                      <button alt="disabled"
                        type="button"
                        title="Cliquez pour annuler les modifications"
                        //style={{ cursor: editingStepIndex !== null ? "not-allowed" : "pointer" }}
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleCancel}
                      >
                        Edition en cours
                      </button>
                    </td>
                  )}

                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ------------- AJOUT / MODIFICATION ETAPE -----------*/}
      <div className="mb-4">
        <a id="stepEdit" style={{ marginTop: "-80px", paddingTop: "80px", display: "block" }}></a>
        <Title alt="Titre modifier / créer"
          text={
            editingStepIndex !== null
              ? `Modifier l'étape${Contents.stepTitle ? ' "' + Contents.stepTitle + '"' : ''}`
              : "Créer une étape"
          }
          className="text-center text-primary"
        />
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div alt="stepTitle" className="col-md-6 mb-3" title="Nom de l'étape, 
            telle qu'elle sera affichée 
            dans l'interface utilisateur'">
              <TextInput
                label="Titre de l'étape ℹ️"
                placeholder="Ex : Démontage de ..."
                value={Contents.stepTitle}
                onChange={(e) => handleChange("stepTitle", e.target.value)}
              />
            </div>
            <div alt="stepNote" className="col-md-6 mb-3" title="Note informative de l'étape (optionnelle)
            affichée dans la UI, en dessous de la description de l'étape">
              <TextInput
                label="Note ℹ️"
                placeholder="Ex : information complémentaire pour l'étape"
                value={Contents.stepNote}
                onChange={(e) => handleChange("stepNote", e.target.value)}
              />
            </div>
            <div alt="stepDesc" className="col-md-6 mb-3" title="Description de l'étape,
            telle qu'elle sera affichée
             dans l'interface utilisateur">
              <TextArea
                label="Description ℹ️"
                placeholder="Détails de l'étape"
                value={Contents.stepDesc}
                onChange={(e) => handleChange("stepDesc", e.target.value)}
              />
            </div>
            <div alt="stepAudioText" className="col-md-6" title="Texte à convertir en commentaire audio.
            Fournit à l'utilisateur des informations complémentaires
            sur l'étape en cours.">
              <TextArea
                label="Texte audio ℹ️"
                placeholder="Texte du commentaire audio"
                value={Contents.stepAudioText}
                onChange={(e) =>
                  handleChange("stepAudioText", e.target.value)
                }
              />
            </div>
          </div>
          {/* ---- Gestion des fichiers ---- */}
          <div className="row mb-3">
            <div alt="Img Desc" className="col-md-3" title="Image de description de l'étape (optionnelle).
            👉 Cette image est remplacée dynamiquement par celle de l'action en cours.">
              <label><i className="mdi mdi-image-frame" /> Image de l'étape (png/jpg) ℹ️</label>
              {/* ---- Gestion de l'IMAGE ---- */}
              {Contents.stepImg ? (
                <div alt="upload file" className="d-flex align-items-center">
                  <input alt="replace file"
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    className="form-control"
                    style={{ width: "140px" }}
                    title='♻️ Remplacer le fichier existant'
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const pngFile = await convertToPng(file);
                        setPendingImg(pngFile);
                      }
                    }}
                  />
                  <div alt="delete file">
                    {/*<span className="text-primary small fw-bold ms-2">[Fichier existant]</span> */}
                    <img
                      src={previewImgUrl ? previewImgUrl : `/api/data/${folderName}/${Contents.stepImg}`}
                      alt="Vignette"
                      style={{
                        maxWidth: "60px",
                        maxHeight: "60px",
                        marginLeft: "10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        cursor: "pointer"
                      }}
                      title="Cliquez pour agrandir"
                      onClick={() => {
                        setModalImgUrl(
                          previewImgUrl
                            ? previewImgUrl
                            : `/api/data/${folderName}/${Contents.stepImg}`
                        );
                        setShowImgModal(true);
                      }}
                    />

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => handleDeleteFile("stepImg")}
                      title={`❌ Supprimer le fichier existant\n(${Contents.stepImg})`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input alt="upload file"
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    className="form-control"
                    title='📥 Ajouter un fichier'
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const pngFile = await convertToPng(file);
                        setPendingImg(pngFile);
                      }
                    }}
                  />
                  {previewImgUrl && (
                    <img
                      src={previewImgUrl}
                      alt="Vignette"
                      style={{ maxWidth: "60px", maxHeight: "60px", marginTop: "10px", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                  )}
                </>
              )}
            </div>
            {/* ---- Gestion de l'HOLOGRAMME ---- */}
            <div alt="stepHologram" className="col-md-3"
              title="Modèle 3D secondaire au format .glb qui permet, au choix :
            👉 De mettre à jour l'hologramme principal À LA FIN de l'étape.
            👉 D'afficher un hologramme additionnel manipulable PENDANT l'étape.">
              <label><i className="mdi mdi-cube-outline" /> Hologramme secondaire (glb) ℹ️</label>

              {Contents.stepHologram ? (
                <div alt="Secondary hologram" className="d-flex align-items-center">
                  <input alt="replace file"
                    type="file"
                    accept=".glb"
                    className="form-control"
                    style={{ width: "140px" }}
                    title='♻️ Remplacer le fichier existant'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setPendingHologram(file);
                    }}
                  />
                  <div alt="delete file">
                    {/* <span className="text-primary small fw-bold ms-2">[Fichier existant]</span> */}
                    {/* 👇 BOUTON DOWNLOAD */}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success ms-2"
                      onClick={() => handleDownloadHologram(Contents.stepHologram)}
                      title={`⬇️ Télécharger le fichier\n(${Contents.stepHologram})`}
                    >
                      <FaDownload />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => handleDeleteFile("stepHologram")}
                      title={`❌ Supprimer le fichier existant\n(${Contents.stepHologram})`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : (
                <input alt="replace file"
                  type="file"
                  accept=".glb"
                  className="form-control"
                  title='📥 Ajouter un fichier'
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPendingHologram(file);
                    }
                  }}
                />
              )}
              {/* interactable / non-interactable */}
              {(!!pendingHologram || !!Contents.stepHologram) && (
                <div alt="gestion de l'hologramme" className="row">
                  <div alt="interactable" className="col-md-12">
                    <select
                      className="form-select form-select-sm mt-2"
                      value={Contents.stepHologramInteractable ? "true" : "false"}
                      onChange={(e) => handleChange("stepHologramInteractable", e.target.value === "true")}
                    >
                      <option value="false" title="Remplace l'hologramme principal à la fin de cette étape,
                      pour actualiser l'avancement du processus.
                      (ex : un ordinateur sans sa plaque de fond.)">Hologramme d'actualisation</option>
                      <option value="true" title="L'hologramme manipulable par l'utilisateur pendant l'étape.
                      Les actions sont indiquées sur cet objet
                      (ex : un disque sur lequel on doit fixer un chassis)">Hologramme additionnel</option>
                    </select>
                  </div>
                  {/*{!Contents.stepHologramInteractable && (
                  <div alt="pos/rot" className="col-md-4 mt-3">
                    <span className="me-2 small text-secondary" title="position X,Y,Z">
                      [{Contents.stepHologramOffsetPos.x},{Contents.stepHologramOffsetPos.y},{Contents.stepHologramOffsetPos.z}]
                    </span>
                    <span className="me-2 small text-secondary" title="rotation X,Y,Z">
                      [{Contents.stepHologramOffsetRot.x},{Contents.stepHologramOffsetRot.y},{Contents.stepHologramOffsetRot.z}]
                    </span>
                  </div>
                  )}*/}
                </div>
              )}
            </div>
            {/* ---- Gestion de l'AUDIO ---- */}
            <div alt="Fichier Audio" className="col-md-6" title="Fichier audio du commentaire.
            👉 Pour remplacer un audio, vous devez d'abord supprimer l'existant." >
              <label className="form-label"><i className="mdi mdi-volume-high" /> Audio (ogg) ℹ️</label>
              {Contents.stepAudio ? (
                <div alt="upload file" className="d-flex flex-column align-items-start">
                  {/*<input alt="replace file"
                    type="file"
                    accept=".ogg"
                    className="form-control"
                    title='📥 Choisir un fichier (remplacer)'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPendingAudio(file);
                      }
                    }}
                  />*/}
                  {previewAudioUrl && (
                    <audio
                      controls
                      src={previewAudioUrl}
                      style={{ maxWidth: "100%", marginTop: "-10px" }}
                    >
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                  )}
                  {!previewAudioUrl && (
                    <div alt="delete file" >
                      <audio
                        controls
                        src={`/api/data/${folderName}/${Contents.stepAudio}`}
                        style={{ maxWidth: "100%", marginTop: "-10px" }}
                        onLoadedData={e => console.log('Audio URL:', e.target.src)}
                      >
                        Votre navigateur ne supporte pas l'audio.
                      </audio>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ms-2"
                        style={{ marginTop: "-50px" }}
                        onClick={() => handleDeleteFile("stepAudio")}
                        title={`❌ Supprimer le fichier existant \n(${Contents.stepAudio})`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div alt="upload file" className="d-flex flex-column align-items-start">
                  <input alt="upload file"
                    type="file"
                    accept=".ogg"
                    className="form-control"
                    title='📥 Ajouter un fichier audio'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPendingAudio(file);
                      }
                    }}
                  />
                  {previewAudioUrl && (
                    <audio
                      controls
                      src={previewAudioUrl}
                      style={{ maxWidth: "100%" }}
                    >
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Composant ActionManager pour gérer toutes les actions */}
          <ActionManage
            Contents={Contents}
            setContents={setContents}
            editingStepIndex={editingStepIndex}
            folderName={folderName}
            getNextStepID={getNextStepID}
            setMessage={setMessage}
            onUpload={handleUploadFile}
            onDelete={handleDeleteFile}
            detailsRef={detailsRef}
            // envoie des props à actionManage
            pendingImg={pendingImg}
            setPendingImg={setPendingImg}
            pendingAudio={pendingAudio}
            setPendingAudio={setPendingAudio}
            pendingHologram={pendingHologram}
            setPendingHologram={setPendingHologram}
          />

          {/* ------------ BOUTON GESTION DE L'ETAPE ------------- */}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              type="button"
              text={editingStepIndex !== null ? "Annuler les modifications" : "Annuler"}
              className="btn btn-lg btn-outline-secondary"
              onClick={handleCancel}
            />
            <Button
              text={
                !Contents.stepTitle
                  ? "⚠️ Titre de l'étape manquante ⚠️"
                  : editingStepIndex !== null
                    ? `Sauvegarder l'étape "${Contents.stepTitle}"`
                    : `Créer l'étape "${Contents.stepTitle}"`
              }
              type="submit"
              className="btn btn-lg btn-outline-primary"
              disabled={
                Contents.stepTitle.trim() === ""
              }
            />
          </div>
        </form>
      </div >
      {/*editingStepIndex !== null && (Contents?.stepActions?.length === 0 || !Contents?.stepActions) && (
        <div style={{ height: "100px" }} /> // Cette linge permet de garder l'espace pour le bouton d'annulation
      )*/}
      {showImgModal && (
        <div
          onClick={() => setShowImgModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "zoom-out"
          }}
        >
          <img
            src={modalImgUrl}
            alt="Agrandissement"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
              boxShadow: "0 0 20px rgba(0,0,0,0.5)"
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </div >
  );
};

export default StepsManage;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license